import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { CityService, CityItem, SelectedCity, isBackendCityId } from '../services/city.service';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';
import { LocationUpdateModalService } from '../services/location-update-modal.service';

@Component({
  selector: 'app-city-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './city-onboarding.component.html',
  styleUrl: './city-onboarding.component.css'
})
export class CityOnboardingComponent implements OnInit, OnDestroy {
  overlayOpen = false;
  /** Filter for the same city list used in Discover / header API. */
  citySearch = '';
  showAllCities = false;

  private openSub?: Subscription;
  private citiesSub?: Subscription;
  private lastOverlay = false;
  detectingUserLocation = false;
  dectedLocation = '';
  userSelectedLocation = '';
  detectLocationError = '';

  constructor(
    public cityService: CityService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private dataService: DataService,
    private locationUpdateModalService: LocationUpdateModalService
  ) { }

  ngOnInit(): void {
    this.cityService.loadCities();

    this.openSub = combineLatest([
      this.cityService.selectedCity$,
      this.cityService.cityPickerOpen$
    ]).subscribe(([city, pickerOpen]) => {
      const nextOpen = !city || pickerOpen;
      if (nextOpen && !this.lastOverlay) {
        this.citySearch = '';
        this.showAllCities = false;
      }
      this.lastOverlay = nextOpen;
      this.overlayOpen = nextOpen;
      this.syncBodyScrollLock();
      this.cdr.detectChanges();
    });

    this.citiesSub = this.cityService.cities$.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.openSub?.unsubscribe();
    this.citiesSub?.unsubscribe();
    document.body.classList.remove('city-onboarding-open');
  }

  get filteredCities(): CityItem[] {
    const q = this.citySearch.trim().toLowerCase();
    const all = this.cityService.cities;
    if (!q) {
      return all;
    }
    return all.filter((c) => {
      const name = (c.name || '').toLowerCase();
      return name.includes(q);
    });
  }

  get popularCities(): CityItem[] {
    const all = this.cityService.cities || [];
    // Prefer those with event_count > 0, else take first 10.
    const sorted = [...all].sort((a, b) => {
      const ea = Number((a as any)?.event_count ?? (a as any)?.events_count ?? 0);
      const eb = Number((b as any)?.event_count ?? (b as any)?.events_count ?? 0);
      return eb - ea;
    });
    return sorted.slice(0, 10);
  }

  toggleViewAll(): void {
    this.showAllCities = !this.showAllCities;
  }

  getCityFlag(city: CityItem): string {
    const flagMap: { [key: string]: string } = {
      'United Arab Emirates': '🇦🇪',
      UAE: '🇦🇪',
      AE: '🇦🇪',
      India: '🇮🇳',
      IN: '🇮🇳',
      'Saudi Arabia': '🇸🇦',
      KSA: '🇸🇦',
      SA: '🇸🇦',
      Qatar: '🇶🇦',
      QA: '🇶🇦',
      'United States': '🇺🇸',
      US: '🇺🇸',
      USA: '🇺🇸'
    };
    const country = (city?.country_name || city?.country || '').toString();
    return flagMap[country] || '📍';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.overlayOpen && this.cityService.getSelectedCity()) {
      this.dismiss();
    }
  }

  private syncBodyScrollLock(): void {
    if (this.overlayOpen) {
      document.body.classList.add('city-onboarding-open');
    } else {
      document.body.classList.remove('city-onboarding-open');
    }
  }

  dismiss(): void {
    if (!this.cityService.getSelectedCity()) {
      return;
    }
    this.cityService.closeCityPicker();
  }

  onBackdropClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  pickCity(city: any, type?: string): void {
    const cityName = city?.name ?? city?.city;

    if (!cityName) {
      return;
    }

    const previous = this.cityService.getSelectedCity();
    const next = this.cityService.resolveSelectedCity({
      id: Number(city.id),
      name: city.name || city.city,
      country_name: city.country_name
    });

    if (!next) {
      return;
    }

    if (type === 'detect') {
      this.userSelectedLocation = '';
      this.dectedLocation = next.name;
    }

    if (type === 'user') {
      this.userSelectedLocation = next.name;
      this.dectedLocation = '';
    }

    if (
      previous &&
      Number(previous.id) === Number(next.id) &&
      previous.name?.trim().toLowerCase() === next.name.trim().toLowerCase()
    ) {
      this.cityService.closeCityPicker();
      return;
    }

    const oldCityName = previous?.name?.trim() ?? '';
    const finish = (showSuccessModal: boolean) => {
      this.cityService.setSelectedCity(next);
      this.cityService.closeCityPicker();
      if (showSuccessModal && oldCityName) {
        this.locationUpdateModalService.show(oldCityName, next.name);
      }
      const url = this.router.url.split('?')[0].split('#')[0];
      if (url !== '/' && url !== '') {
        void this.router.navigateByUrl('/');
      }
    };

    if (!this.authService.isAuthenticated()) {
      finish(!!oldCityName);
      return;
    }

    const payload: Record<string, unknown> = { city: next.name };
    if (isBackendCityId(next.id)) {
      payload['city_id'] = next.id;
    }

    this.dataService.postProfile(payload).subscribe({
      next: () => {
        const profile = this.authService.getUserProfile();
        if (profile) {
          this.authService.setUserProfile({
            ...profile,
            city: next.name,
            city_id: isBackendCityId(next.id) ? next.id : profile.city_id
          });
        }
        finish(!!oldCityName);
      },
      error: () => {
        // Still apply local city so listings match selection; skip success modal on API failure.
        finish(false);
      }
    });
  }

  detectLocation() {
    this.detectingUserLocation = true;
    this.detectLocationError = '';
    this.cityService.detectLocation().subscribe({
      next: (res: any) => {
        try {
          const detectedLocation = this.extractDetectedLocationRecord(res);
          const detectedCity = this.normalizeDetectedCity(detectedLocation);
          if (!detectedCity) {
            throw new Error('Location city not found');
          }
          const normalizedLocation = {
            id: detectedLocation?.['id'],
            name: detectedCity,
            city: detectedCity,
            country_name: detectedLocation?.['country_name'] ?? detectedLocation?.['country']
          };
          this.dectedLocation = detectedCity;
          this.pickCity(normalizedLocation, 'detect');
          this.detectingUserLocation = false;
        } catch (error) {
          // console.log('detectLocation parse failed', error);
          this.detectingUserLocation = false;
          this.detectLocationError =
            'We could not detect your location. Please try again.';
        }
      },
      error: (error: any) => {
        console.error('adminOverview API error', error);
        this.detectingUserLocation = false;
        this.detectLocationError =
          error?.message ||
          error?.error?.['message'] ||
          error?.error?.['msg'] ||
          'We could not detect your location. Please try again.';
      }
    });
  }

  private extractDetectedLocationRecord(response: unknown): Record<string, unknown> {
    if (!response || typeof response !== 'object') {
      return {};
    }

    const record = response as Record<string, unknown>;
    const data = record['data'];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const inner = data as Record<string, unknown>;
      const nestedData = inner['data'];
      if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
        return nestedData as Record<string, unknown>;
      }
      return inner;
    }

    return record;
  }

  private extractDetectedCityName(location: Record<string, unknown>): string {
    const rawCity = String(
      location['city'] ??
      location['city_name'] ??
      location['name'] ??
      location['location'] ??
      ''
    ).trim();
    if (!rawCity) {
      return '';
    }

    return rawCity.split(/[,/|-]+/)[0]?.trim() ?? rawCity;
  }

  private normalizeDetectedCity(location: Record<string, unknown>): string {
    const detectedCity = this.extractDetectedCityName(location);
    if (!detectedCity) {
      return '';
    }

    const geoTokens = this.extractDetectedGeoTokens(location);
    if (this.isDelhiNcrLocation(geoTokens)) {
      return 'Delhi NCR';
    }

    return detectedCity;
  }

  private extractDetectedGeoTokens(location: Record<string, unknown>): string[] {
    const values = [
      location['city'],
      location['city_name'],
      location['name'],
      location['location'],
      location['district'],
      location['state'],
      location['state_name'],
      location['region'],
      location['region_name'],
      location['county'],
      location['administrative_area'],
      location['formatted_address']
    ];

    return values
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .flatMap((value) => value.split(/[,/|-]+/))
      .map((value) => this.normalizeGeoToken(value))
      .filter(Boolean);
  }

  private isDelhiNcrLocation(tokens: string[]): boolean {
    if (!tokens.length) {
      return false;
    }

    const delhiNcrKeywords = new Set([
      'delhi',
      'new delhi',
      'delhi ncr',
      'national capital territory of delhi',
      'nct of delhi',
      'gurgaon',
      'gurugram',
      'noida',
      'greater noida',
      'ghaziabad',
      'faridabad',
      'nangloi jat',
      'nangloi'
    ]);

    return tokens.some((token) => {
      if (delhiNcrKeywords.has(token)) {
        return true;
      }

      return token.includes('delhi') || token.includes('ncr');
    });
  }

  private normalizeGeoToken(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ');
  }
}
